import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// Unsaved changes on the Business Settings page (Notification Centre redesign, R8.4). Each form says whether
// it has changes and how to save or undo them; the page shows one save bar while any form has changes, and
// asks before leaving (another area, another page, or closing the tab) when there are some.
//
//   const { form, setForm, loadForm, dirty, discard } = useTrackedForm(empty)   a form loaded from the server:
//        loadForm(values) after each load (also after a save, which reloads), setForm for edits
//   useUnsaved("support", dirty, onSave, discard)                              tells the page about it
//   useDirty("policy-expiry", value, savedValue, onSave, onDiscard)            the same, comparing two values
const UnsavedContext = createContext(null);

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const useTrackedForm = (initial) => {
    const [form, setForm] = useState(initial);
    const [saved, setSaved] = useState(initial);
    const loadForm = useCallback((values) => { setForm(values); setSaved(values); }, []);
    const discard = useCallback(() => setForm(saved), [saved]);
    return { form, setForm, loadForm, saved, dirty: !same(form, saved), discard };
};

export const useUnsaved = (key, dirty, save, discard) => {
    const ctx = useContext(UnsavedContext);
    const ref = useRef({ save, discard });
    ref.current = { save, discard };
    useEffect(() => { if (ctx) ctx.mark(key, dirty ? ref : null); }, [ctx, key, dirty]);
    useEffect(() => () => { if (ctx) ctx.mark(key, null); }, [ctx, key]);
};

export const useDirty = (key, value, savedValue, save, discard) => useUnsaved(key, !same(value, savedValue), save, discard);

export const UnsavedProvider = ({ children }) => {
    const [entries, setEntries] = useState({});
    const mark = useCallback((key, ref) => setEntries((prev) => {
        if ((prev[key] || null) === ref) return prev;
        const next = { ...prev };
        if (ref) next[key] = ref; else delete next[key];
        return next;
    }), []);
    const value = useMemo(() => ({ mark, entries, dirty: Object.keys(entries).length > 0 }), [mark, entries]);
    return <UnsavedContext.Provider value={value}>{children}</UnsavedContext.Provider>;
};

export const useUnsavedState = () => useContext(UnsavedContext);

// Save every form with changes (each shows its own result), or undo them all.
export const saveAll = async (entries) => {
    for (const ref of Object.values(entries)) {
        // one after the other: each form reloads after its save
        // eslint-disable-next-line no-await-in-loop
        await ref.current.save?.();
    }
};
export const discardAll = (entries) => Object.values(entries).forEach((ref) => ref.current.discard?.());

const LEAVE_TEXT = "You have unsaved changes in Business Settings. Leave without saving?";

// While there are changes: closing or reloading the tab asks (the browser's own prompt), and so does a click on
// any link outside the settings panel (the sidebar, the header).
export const useLeaveGuard = (dirty, panelRef) => {
    useEffect(() => {
        if (!dirty) return undefined;
        const onUnload = (e) => { e.preventDefault(); e.returnValue = LEAVE_TEXT; return LEAVE_TEXT; };
        const onClick = (e) => {
            const link = e.target?.closest?.("a[href]");
            if (!link || panelRef.current?.contains(link) || link.target === "_blank") return;
            // eslint-disable-next-line no-alert
            if (!window.confirm(LEAVE_TEXT)) { e.preventDefault(); e.stopPropagation(); }
        };
        window.addEventListener("beforeunload", onUnload);
        document.addEventListener("click", onClick, true);
        return () => { window.removeEventListener("beforeunload", onUnload); document.removeEventListener("click", onClick, true); };
    }, [dirty, panelRef]);
};
